import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSpacer,
  ToolbarCount,
  ToolbarDivider,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import type {
  InvoiceScheduleListItem,
  InvoiceScheduleDetail,
  InvoiceScheduleCreateRequest,
  InvoiceScheduleUpdateRequest,
} from '@techgitter/contracts';
import {
  getInvoiceSchedules,
  getInvoiceSchedule,
  createInvoiceScheduleAndWait,
  updateInvoiceSchedule,
  waitForScheduleLineOps,
  deleteInvoiceSchedule,
} from '../services/invoiceScheduleAdminService';
import { InvoiceScheduleEditPanel } from '../components/admin/InvoiceScheduleDialog';

function formatCurrency(value: number): string {
  return `$${value.toFixed(2)}`;
}

type EditMode = 'list' | 'create' | 'edit';
type SortKey = 'customerName' | 'name' | 'frequency' | 'nextRun' | 'paused' | 'subtotal' | 'lineItemCount';
type SortDir = 'asc' | 'desc';

function compareValues(a: InvoiceScheduleListItem, b: InvoiceScheduleListItem, key: SortKey): number {
  const av = a[key];
  const bv = b[key];
  if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv);
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  if (typeof av === 'boolean' && typeof bv === 'boolean') return (av ? 1 : 0) - (bv ? 1 : 0);
  return 0;
}

export const InvoiceScheduleAdminPage: React.FC = () => {
  const [schedules, setSchedules] = useState<InvoiceScheduleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<EditMode>('list');
  const [editingSchedule, setEditingSchedule] = useState<InvoiceScheduleDetail | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('customerName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadSchedules = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInvoiceSchedules(signal);
      setSchedules(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load invoice schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSchedules(controller.signal);
    return () => controller.abort();
  }, [loadSchedules]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedFiltered = schedules
    .filter((s) => !filter || s.customerName.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      const cmp = compareValues(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleCreate = () => {
    setEditingSchedule(null);
    setEditError(null);
    setEditMode('create');
  };

  const handleEdit = async (item: InvoiceScheduleListItem) => {
    try {
      setEditError(null);
      const detail = await getInvoiceSchedule(item.scheduleId);
      setEditingSchedule(detail);
      setEditMode('edit');
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to load schedule details',
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    setEditMode('list');
    setEditingSchedule(null);
    setEditError(null);
  };

  const handleSave = async (payload: InvoiceScheduleCreateRequest | Omit<InvoiceScheduleUpdateRequest, 'scheduleId'>) => {
    setIsSaving(true);
    setEditError(null);

    try {
      if (editingSchedule) {
        const updated = await updateInvoiceSchedule(
          editingSchedule.scheduleId,
          payload as Omit<InvoiceScheduleUpdateRequest, 'scheduleId'>,
        );
        // Line edits applied synchronously; adds/removes are async orchestrator
        // ops — poll them so we can report the real outcome. They land in RS
        // immediately but only mirror into this view on the next sync.
        const pendingOps = updated.pendingOperations ?? [];
        if (pendingOps.length > 0) {
          setSnackbar({
            open: true,
            message: `Schedule saved — applying ${pendingOps.length} line change(s) in RepairShopr…`,
            severity: 'info',
          });
          const { failed } = await waitForScheduleLineOps(pendingOps);
          setSnackbar(
            failed.length > 0
              ? {
                  open: true,
                  message: `${pendingOps.length - failed.length}/${pendingOps.length} line change(s) applied. Failed: ${failed
                    .map((o) => `${o.op} ${o.name}`)
                    .join(', ')}`,
                  severity: 'error',
                }
              : {
                  open: true,
                  message: 'Schedule updated — line add/remove applied in RepairShopr (appears here after the next sync)',
                  severity: 'success',
                },
          );
        } else {
          setSnackbar({
            open: true,
            message: 'Schedule updated successfully',
            severity: 'success',
          });
        }
      } else {
        // Async: the API enqueues the orchestrator create (RS schedule + DV
        // mirror + line items); wait for it to finish so we can deep-link the
        // new RepairShopr schedule for the manual fix-up settings.
        const result = await createInvoiceScheduleAndWait(payload as InvoiceScheduleCreateRequest);
        if (result.rsScheduleId) {
          const rsUrl = `https://techsolvers.repairshopr.com/schedules/${result.rsScheduleId}/edit`;
          window.open(rsUrl, '_blank');
          setSnackbar({
            open: true,
            message: 'Schedule created — opening RepairShopr to configure required settings',
            severity: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Schedule created successfully',
            severity: 'success',
          });
        }
      }
      setEditMode('list');
      setEditingSchedule(null);
      loadSchedules();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePaused = async (item: InvoiceScheduleListItem) => {
    const previousState = item.paused;
    setSchedules((current) =>
      current.map((s) => (s.scheduleId === item.scheduleId ? { ...s, paused: !s.paused } : s))
    );

    try {
      await updateInvoiceSchedule(item.scheduleId, { paused: !previousState });
    } catch (err) {
      setSchedules((current) =>
        current.map((s) => (s.scheduleId === item.scheduleId ? { ...s, paused: previousState } : s))
      );
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await deleteInvoiceSchedule(deleteConfirmId);
      setSchedules((current) => current.filter((s) => s.scheduleId !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'Schedule deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete',
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteTarget = schedules.find((s) => s.scheduleId === deleteConfirmId);

  // --- Edit / Create mode: show inline form ---
  if (editMode !== 'list') {
    return (
      <>
        <InvoiceScheduleEditPanel
          schedule={editingSchedule}
          isSaving={isSaving}
          error={editError}
          onCancel={handleCancel}
          onSave={handleSave}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // --- List mode ---
  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={filter}
          onSearchChange={setFilter}
          searchPlaceholder="Filter by customer…"
          maxWidth={300}
        />
        <ToolbarSpacer />
        <ToolbarCount>
          {loading
            ? ''
            : filter
              ? `${sortedFiltered.length} of ${schedules.length}`
              : `${schedules.length} invoice schedule${schedules.length !== 1 ? 's' : ''}`}
        </ToolbarCount>
        <ToolbarDivider />
        <ToolbarButton icon={IconRefresh} onClick={() => loadSchedules()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton variant="primary" icon={IconPlus} onClick={handleCreate}>
          Add Schedule
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell width="22%" sortable active={sortKey === 'customerName'} direction={sortDir} onSort={() => handleSort('customerName')}>Customer</DataTableHeadCell>
                <DataTableHeadCell width="26%" sortable active={sortKey === 'name'} direction={sortDir} onSort={() => handleSort('name')}>Schedule Name</DataTableHeadCell>
                <DataTableHeadCell width="8%" sortable active={sortKey === 'frequency'} direction={sortDir} onSort={() => handleSort('frequency')}>Frequency</DataTableHeadCell>
                <DataTableHeadCell width="8%" sortable active={sortKey === 'nextRun'} direction={sortDir} onSort={() => handleSort('nextRun')}>Next Run</DataTableHeadCell>
                <DataTableHeadCell width="7%" sortable active={sortKey === 'paused'} direction={sortDir} onSort={() => handleSort('paused')}>Paused</DataTableHeadCell>
                <DataTableHeadCell width="7%" sortable active={sortKey === 'subtotal'} direction={sortDir} onSort={() => handleSort('subtotal')}>Subtotal</DataTableHeadCell>
                <DataTableHeadCell width="4%" sortable active={sortKey === 'lineItemCount'} direction={sortDir} onSort={() => handleSort('lineItemCount')}>#</DataTableHeadCell>
                <DataTableHeadCell width="8%" align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {sortedFiltered.length === 0 ? (
                <DataTableEmpty colSpan={8}>
                  {schedules.length === 0 ? 'No invoice schedules found' : 'No matches'}
                </DataTableEmpty>
              ) : (
                sortedFiltered.map((item) => (
                  <DataTableRow key={item.scheduleId}>
                    <DataTableCell bold>{item.customerName || '—'}</DataTableCell>
                    <DataTableCell>{item.name}</DataTableCell>
                    <DataTableCell>
                      <Chip>{item.frequency}</Chip>
                    </DataTableCell>
                    <DataTableCell>{item.nextRun || '—'}</DataTableCell>
                    <DataTableCell>
                      <Switch
                        checked={item.paused}
                        onChange={() => handleTogglePaused(item)}
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell>{formatCurrency(item.subtotal)}</DataTableCell>
                    <DataTableCell>{item.lineItemCount}</DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirmId(item.scheduleId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
      </Box>

      <Dialog
        open={deleteConfirmId !== null}
        onClose={isDeleting ? undefined : () => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Invoice Schedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;
            {deleteTarget?.customerName ? ` for ${deleteTarget.customerName}` : ''}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
